import { z } from "zod";

import { UUID } from "../generic/uuid.ts";

// ================================================
// ENUMS
// ================================================

// ImageAttachmentTypeEnum
export const ImageAttachmentTypeEnum = z.enum(["image"]);
export type ImageAttachmentTypeEnum = z.infer<typeof ImageAttachmentTypeEnum>;

// DocumentAttachmentTypeEnum
export const DocumentAttachmentTypeEnum = z.enum(["document"]);
export type DocumentAttachmentTypeEnum = z.infer<
  typeof DocumentAttachmentTypeEnum
>;

// LinkAttachmentTypeEnum
export const LinkAttachmentTypeEnum = z.enum(["link"]);
export type LinkAttachmentTypeEnum = z.infer<typeof LinkAttachmentTypeEnum>;

// AttachmentTypeEnum
export const AttachmentTypeEnum = z.union([
  ImageAttachmentTypeEnum,
  DocumentAttachmentTypeEnum,
  LinkAttachmentTypeEnum,
]);
export type AttachmentTypeEnum = z.infer<typeof AttachmentTypeEnum>;

// ImageAttachmentMimeTypeEnum
export const ImageAttachmentMimeTypeEnum = z.enum([
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/webp",
]);
export type ImageAttachmentMimeTypeEnum = z.infer<
  typeof ImageAttachmentMimeTypeEnum
>;

// DocumentAttachmentMimeTypeEnum
export const DocumentAttachmentMimeTypeEnum = z.enum([
  "application/pdf",
  "text/markdown",
  "text/plain",
  "text/javascript",
]);
export type DocumentAttachmentMimeTypeEnum = z.infer<
  typeof DocumentAttachmentMimeTypeEnum
>;

// AttachmentMimeTypeEnum
export const AttachmentMimeTypeEnum = z.union([
  ImageAttachmentMimeTypeEnum,
  DocumentAttachmentMimeTypeEnum,
]);
export type AttachmentMimeTypeEnum = z.infer<typeof AttachmentMimeTypeEnum>;

// ================================================
// CONSTANTS
// ================================================

export const ATTACHMENT_MAX_COUNT: number = 5;
export const ATTACHMENT_MAX_PDF_PAGE_COUNT: number = 100;
export const ATTACHMENT_MAX_SIZE_MB = 3; // 3 MB - Anthropic's max image file size limit is 5 MB. However, image files are converted to base64 encoded strings, which take more space, so we set the limit to 3 MB.
export const ATTACHMENT_MAX_SIZE = ATTACHMENT_MAX_SIZE_MB * 1024 * 1024;
export const ATTACHMENT_SUPPORTED_MIME_TYPES: AttachmentMimeTypeEnum[] = [
  ...DocumentAttachmentMimeTypeEnum.options,
  ...ImageAttachmentMimeTypeEnum.options.filter((type) => type !== "image/gif"), // TODO: Remove filter after support for image/gif (non-animated GIFs) has been added to the UI
];

// Canonical file extension for each supported MIME type (e.g. for naming uploaded files).
export const ATTACHMENT_MIME_TYPE_EXTENSIONS: Record<
  AttachmentMimeTypeEnum,
  string
> = {
  "image/gif": "gif",
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "application/pdf": "pdf",
  "text/markdown": "md",
  "text/plain": "txt",
  "text/javascript": "js",
};

// ================================================
// SELECT
// ================================================

// AttachmentBase
const AttachmentBase = z
  .strictObject({
    id: UUID,
    message_id: UUID,
    created_at: z.string(),
    updated_at: z.string(),
  })
  .refine((data) => data.created_at <= data.updated_at, {
    message: '"created_at" must be before or equal to "updated_at"',
  });

// FileAttachmentBase
const FileAttachmentBase = AttachmentBase.safeExtend({
  path: z.string().trim().min(1),
  url: z.null(),
  name: z.string().trim().min(1),
  size: z.int().min(1).max(ATTACHMENT_MAX_SIZE),
});

// ImageAttachment
export const ImageAttachment = FileAttachmentBase.safeExtend({
  type: ImageAttachmentTypeEnum,
  mime_type: ImageAttachmentMimeTypeEnum,
});
export type ImageAttachment = z.infer<typeof ImageAttachment>;

// DocumentAttachment
export const DocumentAttachment = FileAttachmentBase.safeExtend({
  type: DocumentAttachmentTypeEnum,
  mime_type: DocumentAttachmentMimeTypeEnum,
});
export type DocumentAttachment = z.infer<typeof DocumentAttachment>;

// LinkAttachment
export const LinkAttachment = AttachmentBase.safeExtend({
  type: LinkAttachmentTypeEnum,
  path: z.null(),
  url: z.httpUrl(), // http or https URLs only
  name: z.null(),
  size: z.null(),
  mime_type: z.null(),
});
export type LinkAttachment = z.infer<typeof LinkAttachment>;

// Attachment
export const Attachment = z.discriminatedUnion("type", [
  ImageAttachment,
  DocumentAttachment,
  LinkAttachment,
]);
export type Attachment = z.infer<typeof Attachment>;

// AttachmentWithData
export const ImageAttachmentWithData = ImageAttachment.safeExtend({
  data: z.base64(),
});
export type ImageAttachmentWithData = z.infer<typeof ImageAttachmentWithData>;

export const DocumentAttachmentWithData = DocumentAttachment.safeExtend({
  data: z.base64(),
});
export type DocumentAttachmentWithData = z.infer<
  typeof DocumentAttachmentWithData
>;

export const LinkAttachmentWithData = LinkAttachment.safeExtend({
  data: z.null(),
});
export type LinkAttachmentWithData = z.infer<typeof LinkAttachmentWithData>;

export const AttachmentWithData = z.discriminatedUnion("type", [
  ImageAttachmentWithData,
  DocumentAttachmentWithData,
  LinkAttachmentWithData,
]);
export type AttachmentWithData = z.infer<typeof AttachmentWithData>;

// ================================================
// INSERT
// ================================================

const omitInsertFields = {
  created_at: true,
  updated_at: true,
} as const;

// ImageAttachmentInsert
export const ImageAttachmentInsert = ImageAttachment.omit(
  omitInsertFields,
).partial({ id: true });
export type ImageAttachmentInsert = z.infer<typeof ImageAttachmentInsert>;

// DocumentAttachmentInsert
export const DocumentAttachmentInsert = DocumentAttachment.omit(
  omitInsertFields,
).partial({ id: true });
export type DocumentAttachmentInsert = z.infer<typeof DocumentAttachmentInsert>;

// LinkAttachmentInsert
export const LinkAttachmentInsert = LinkAttachment.omit(
  omitInsertFields,
).partial({ id: true });
export type LinkAttachmentInsert = z.infer<typeof LinkAttachmentInsert>;

// AttachmentInsert
export const AttachmentInsert = z.discriminatedUnion("type", [
  ImageAttachmentInsert,
  DocumentAttachmentInsert,
  LinkAttachmentInsert,
]);
export type AttachmentInsert = z.infer<typeof AttachmentInsert>;
